/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PER-PROGRAM MASTER PIPELINE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Before this module every program shared one hardcoded 10-stage array
 * (`MASTER_STAGES` in src/components/MasterPipeline.tsx), referenced by 17
 * files including the admin dashboard, batch centre, email templates, reports
 * and the referrer views. A "Study Abroad UK" application therefore moved
 * through DS-2019 and visa stages that do not exist for that program.
 *
 * Each program now owns its own ordered stage list. The historical array is
 * preserved as `DEFAULT_PIPELINE` so:
 *   • programs that have not been configured yet behave exactly as before, and
 *   • the numeric `application.masterStage` index stays meaningful.
 *
 * Stage identity is the `id`, not the index. `stageIndex()` / `stageAtIndex()`
 * bridge to the legacy numeric field so migration can be incremental.
 */

/* ─── Types ───────────────────────────────────────────────────────────────── */

export interface PipelineStage {
  id: string;
  label: string;
  /** Short caption under the label in the chevron bar. */
  subtitle?: string;
  /** Longer explanation shown in tooltips and the stage editor. */
  description?: string;
  /** Hex colour for the chevron. Falls back to the generated gradient. */
  color?: string;
  /** Lucide icon name, e.g. "plane" — optional, purely decorative. */
  icon?: string;
  /** Archived stages stay in historical data but are hidden from pickers. */
  archived?: boolean;
  /** Terminal stages (alumni, withdrawn) end the active workflow. */
  terminal?: boolean;
}

export type PipelineMergeStrategy = 'overwrite' | 'merge';

export interface PipelineTemplate {
  version: 1;
  name: string;
  sourceProgramId?: string;
  sourceProgramName?: string;
  exportedAt: string;
  stages: PipelineStage[];
  defaultStageId?: string;
}

/* ─── The historical default ──────────────────────────────────────────────── */

/**
 * The original hardcoded pipeline, kept verbatim so existing `masterStage`
 * indices keep resolving to the same labels for programs with no custom
 * configuration.
 */
export const DEFAULT_PIPELINE: PipelineStage[] = [
  { id: 'draft', label: 'Draft Application', subtitle: 'Initial application created' },
  { id: 'screening', label: 'Submitted For Screening', subtitle: 'Under initial review' },
  { id: 'qualification', label: 'Qualification', subtitle: 'Evaluating credentials' },
  { id: 'match', label: 'Partner Match', subtitle: 'Seeking host placement' },
  { id: 'placement', label: 'Placement Completed', subtitle: 'Host confirmed' },
  { id: 'immigration', label: 'Immigration', subtitle: 'DS-2019 & visa prep' },
  { id: 'flight', label: 'Flight Confirmation', subtitle: 'Travel arrangements' },
  { id: 'visa', label: 'Visa Completed', subtitle: 'Visa secured' },
  { id: 'started', label: 'Program Started', subtitle: 'Program in progress' },
  { id: 'alumni', label: 'Alumni', subtitle: 'Program completed', terminal: true },
];

/**
 * Starter pipelines offered when configuring a new program. These are
 * suggestions an admin can apply and then edit — never applied automatically.
 */
export const PIPELINE_PRESETS: Array<{ id: string; name: string; description: string; stages: PipelineStage[] }> = [
  {
    id: 'exchange-visitor',
    name: 'Exchange visitor (J-1)',
    description: 'Full sponsorship workflow with placement, DS-2019 and visa stages.',
    stages: DEFAULT_PIPELINE,
  },
  {
    id: 'study-abroad',
    name: 'Study abroad',
    description: 'Admission-led workflow: offer, acceptance, study permit, enrolment.',
    stages: [
      { id: 'draft', label: 'Draft Application', subtitle: 'Initial application created' },
      { id: 'screening', label: 'Document Review', subtitle: 'Verifying submitted documents' },
      { id: 'institution-apply', label: 'Institution Application', subtitle: 'Submitted to school' },
      { id: 'offer', label: 'Offer Received', subtitle: 'Conditional or unconditional offer' },
      { id: 'acceptance', label: 'Offer Accepted', subtitle: 'Applicant confirmed place' },
      { id: 'tuition', label: 'Tuition & Funding', subtitle: 'Deposit and proof of funds' },
      { id: 'study-permit', label: 'Study Permit', subtitle: 'Visa / permit application' },
      { id: 'permit-approved', label: 'Permit Approved', subtitle: 'Authorisation granted' },
      { id: 'travel', label: 'Travel & Arrival', subtitle: 'Flights and arrival plan' },
      { id: 'enrolled', label: 'Enrolled', subtitle: 'Studies underway', terminal: true },
    ],
  },
  {
    id: 'placement-only',
    name: 'Placement only',
    description: 'Lightweight workflow for programs without immigration handling.',
    stages: [
      { id: 'draft', label: 'Draft Application', subtitle: 'Initial application created' },
      { id: 'screening', label: 'Screening', subtitle: 'Under review' },
      { id: 'interview', label: 'Interview', subtitle: 'Employer interview stage' },
      { id: 'offer', label: 'Offer', subtitle: 'Offer extended' },
      { id: 'placement', label: 'Placement Confirmed', subtitle: 'Start date agreed' },
      { id: 'completed', label: 'Completed', subtitle: 'Placement finished', terminal: true },
    ],
  },
];

/* ─── Colour ramp ─────────────────────────────────────────────────────────── */

const RAMP_START = { r: 0x7b, g: 0x9f, b: 0xd4 };
const RAMP_END = { r: 0x78, g: 0xc8, b: 0x60 };
export const INACTIVE_STAGE_COLOR = '#DDE3EC';

/**
 * The original component hardcoded exactly ten colours, so an 11th stage
 * rendered transparent. The ramp is now interpolated for any stage count.
 */
export function stageColor(index: number, total: number): string {
  if (total <= 1) return `#${RAMP_START.r.toString(16)}${RAMP_START.g.toString(16)}${RAMP_START.b.toString(16)}`;
  const t = Math.min(1, Math.max(0, index / (total - 1)));
  const channel = (from: number, to: number) => Math.round(from + (to - from) * t);
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(channel(RAMP_START.r, RAMP_END.r))}${hex(channel(RAMP_START.g, RAMP_END.g))}${hex(
    channel(RAMP_START.b, RAMP_END.b)
  )}`;
}

/* ─── Normalisation ───────────────────────────────────────────────────────── */

function stageId(label: string, index: number): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `stage-${index + 1}`;
}

/** Accepts `string[]` labels or partial stage objects from any older writer. */
export function normalisePipeline(value: unknown): PipelineStage[] {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const stages: PipelineStage[] = [];

  value.forEach((entry, index) => {
    let stage: PipelineStage | null = null;

    if (typeof entry === 'string') {
      const label = entry.trim();
      if (label) stage = { id: stageId(label, index), label };
    } else if (entry && typeof entry === 'object') {
      const raw = entry as Record<string, unknown>;
      const label = String(raw.label ?? raw.name ?? raw.title ?? '').trim();
      if (label) {
        stage = {
          id: String(raw.id ?? '').trim() || stageId(label, index),
          label,
          subtitle: raw.subtitle ? String(raw.subtitle) : undefined,
          description: raw.description ? String(raw.description) : undefined,
          color: raw.color ? String(raw.color) : undefined,
          icon: raw.icon ? String(raw.icon) : undefined,
          archived: raw.archived === true,
          terminal: raw.terminal === true,
        };
      }
    }

    if (!stage) return;
    // Guarantee unique ids — duplicates silently broke index lookups before.
    let uniqueId = stage.id;
    let suffix = 2;
    while (seen.has(uniqueId)) uniqueId = `${stage.id}-${suffix++}`;
    seen.add(uniqueId);
    stages.push({ ...stage, id: uniqueId });
  });

  return stages;
}

/* ─── Resolution ──────────────────────────────────────────────────────────── */

interface PipelineOwner {
  pipeline?: PipelineStage[];
  defaultStageId?: string;
}

/**
 * The pipeline for a program: its own stages when configured, otherwise the
 * platform default. Archived stages are excluded from the working view.
 */
export function getPipelineStages(
  program: PipelineOwner | null | undefined,
  fallback: PipelineStage[] = DEFAULT_PIPELINE
): PipelineStage[] {
  const own = (program?.pipeline || []).filter((stage) => !stage.archived);
  return own.length ? own : fallback;
}

/** Includes archived stages — needed to render historical applications. */
export function getAllPipelineStages(
  program: PipelineOwner | null | undefined,
  fallback: PipelineStage[] = DEFAULT_PIPELINE
): PipelineStage[] {
  const own = program?.pipeline || [];
  return own.length ? own : fallback;
}

export function getDefaultStage(
  program: PipelineOwner | null | undefined,
  fallback: PipelineStage[] = DEFAULT_PIPELINE
): PipelineStage | null {
  const stages = getPipelineStages(program, fallback);
  if (!stages.length) return null;
  if (program?.defaultStageId) {
    const match = stages.find((stage) => stage.id === program.defaultStageId);
    if (match) return match;
  }
  return stages[0];
}

export function getDefaultStageIndex(
  program: PipelineOwner | null | undefined,
  fallback: PipelineStage[] = DEFAULT_PIPELINE
): number {
  const stages = getPipelineStages(program, fallback);
  const stage = getDefaultStage(program, fallback);
  if (!stage) return 0;
  const index = stages.findIndex((s) => s.id === stage.id);
  return index < 0 ? 0 : index;
}

export function stageAtIndex(stages: PipelineStage[], index: number | undefined | null): PipelineStage | null {
  if (index == null || index < 0) return null;
  return stages[index] ?? null;
}

export function stageIndex(stages: PipelineStage[], id: string | undefined | null): number {
  if (!id) return -1;
  return stages.findIndex((stage) => stage.id === id);
}

/**
 * Human label for an application's stage.
 * Replaces `MASTER_STAGES[app.masterStage]?.label`, which returned undefined
 * whenever a program had a different stage count.
 */
export function stageLabel(
  stages: PipelineStage[],
  index: number | undefined | null,
  fallbackLabel = ''
): string {
  const stage = stageAtIndex(stages, index);
  return stage?.label || fallbackLabel;
}

/* ─── Editing ─────────────────────────────────────────────────────────────── */

export function createStage(label = 'New stage'): PipelineStage {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return { id: `stage_${rand}`, label, subtitle: '' };
}

export function moveStage(stages: PipelineStage[], from: number, to: number): PipelineStage[] {
  if (from === to || from < 0 || to < 0 || from >= stages.length || to >= stages.length) return stages;
  const next = [...stages];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/* ─── Templates: copy, duplicate, import, export ──────────────────────────── */

export function exportPipelineTemplate(
  stages: PipelineStage[],
  options: { name: string; programId?: string; programName?: string; defaultStageId?: string }
): PipelineTemplate {
  return {
    version: 1,
    name: options.name,
    sourceProgramId: options.programId,
    sourceProgramName: options.programName,
    exportedAt: new Date().toISOString(),
    defaultStageId: options.defaultStageId,
    stages: stages.map((stage) => ({ ...stage })),
  };
}

export function serialisePipelineTemplate(template: PipelineTemplate): string {
  return JSON.stringify(template, null, 2);
}

/** Parses an exported template. Returns a reason string instead of throwing. */
export function parsePipelineTemplate(json: string): { template: PipelineTemplate } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { error: 'That is not valid JSON.' };
  }
  if (!parsed || typeof parsed !== 'object') return { error: 'Template must be a JSON object.' };

  const raw = parsed as Record<string, unknown>;
  const stages = normalisePipeline(raw.stages ?? raw.pipeline);
  if (!stages.length) return { error: 'No stages found in this template.' };

  return {
    template: {
      version: 1,
      name: String(raw.name || 'Imported pipeline'),
      sourceProgramId: raw.sourceProgramId ? String(raw.sourceProgramId) : undefined,
      sourceProgramName: raw.sourceProgramName ? String(raw.sourceProgramName) : undefined,
      exportedAt: String(raw.exportedAt || new Date().toISOString()),
      defaultStageId: raw.defaultStageId ? String(raw.defaultStageId) : undefined,
      stages,
    },
  };
}

/**
 * Applies a template to an existing pipeline.
 *
 *  • `overwrite` — the incoming stages become the pipeline verbatim.
 *  • `merge`     — existing stages are kept (and keep their customised label,
 *                  colour and description); incoming stages not already present
 *                  are appended. Nothing is ever silently deleted, so applying
 *                  a template to a live program cannot orphan applications
 *                  sitting on a stage that the template omits.
 */
export function applyPipelineTemplate(
  current: PipelineStage[],
  incoming: PipelineStage[],
  strategy: PipelineMergeStrategy
): PipelineStage[] {
  if (strategy === 'overwrite') return incoming.map((stage) => ({ ...stage }));

  const byId = new Map(current.map((stage) => [stage.id, stage]));
  const merged = current.map((stage) => ({ ...stage }));
  for (const stage of incoming) {
    if (!byId.has(stage.id)) merged.push({ ...stage });
  }
  return merged;
}

/**
 * Stages that exist today but are missing from the template — the admin must
 * be warned before an overwrite, because applications may be sitting on them.
 */
export function stagesLostByOverwrite(
  current: PipelineStage[],
  incoming: PipelineStage[]
): PipelineStage[] {
  const incomingIds = new Set(incoming.map((stage) => stage.id));
  return current.filter((stage) => !incomingIds.has(stage.id));
}

/**
 * Remaps a numeric masterStage from one pipeline to another by stage id,
 * so copying a pipeline does not scramble in-flight applications.
 */
export function remapStageIndex(
  fromStages: PipelineStage[],
  toStages: PipelineStage[],
  index: number | undefined | null
): number {
  const stage = stageAtIndex(fromStages, index);
  if (!stage) return 0;
  const next = stageIndex(toStages, stage.id);
  return next >= 0 ? next : Math.min(index ?? 0, Math.max(0, toStages.length - 1));
}
